import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Landmark, MinusCircle, PlusCircle, ShieldAlert } from 'lucide-react';
import {
  formatIqd,
  fromMillidinars,
  type SafeBalanceDto,
  type SafeTransactionDto,
} from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { RecordSafeTransactionDialog } from './RecordSafeTransactionDialog';
import { useSafeBalance, useSafeTransactions } from './useSafe';

/**
 * The safe (SPEC §12.1, wave 3): the venue's main account as a numbered
 * voucher ledger.
 *
 * The screen leads with the reconciliation, not the balance: a cached figure
 * on its own is exactly what the old system showed, and the whole point of
 * the ledger is that the two can now be compared. Drift is shown loudly and
 * left alone — a safe that "fixes itself" is a safe nobody can audit.
 */
export function SafePage() {
  const { t } = useTranslation();
  const { can, venue } = useAuth();
  const currencySymbol = venue?.currencySymbol ?? 'IQD';

  const { data: balance, error, loading, reload } = useSafeBalance();
  const {
    data: transactions,
    error: transactionsError,
    reload: reloadTransactions,
  } = useSafeTransactions();

  const [recording, setRecording] = useState<'OWNER_WITHDRAWAL' | 'OWNER_DEPOSIT' | null>(null);
  const canManage = can('safe.manage');

  if (!can('safe.view')) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold text-content">{t('safe.title')}</h1>
        <p className="mt-2 text-sm text-content-muted">{t('safe.noViewPermission')}</p>
      </div>
    );
  }

  function handleRecorded(): void {
    setRecording(null);
    void reload();
    void reloadTransactions();
  }

  return (
    <div className="flex h-full flex-col overflow-auto">
      <header className="flex flex-wrap items-center gap-3 p-4">
        <h1 className="text-xl font-semibold text-content">{t('safe.title')}</h1>
        <p className="text-xs text-content-muted">{t('safe.subtitle')}</p>
        <div className="flex-1" />
        <Link
          to="/bonds"
          className="inline-flex items-center gap-2 rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover"
        >
          <Landmark className="h-4 w-4" aria-hidden />
          {t('safe.openBonds')}
        </Link>
        <button
          type="button"
          onClick={() => setRecording('OWNER_DEPOSIT')}
          disabled={!canManage || !balance}
          title={canManage ? undefined : t('safe.noManagePermission')}
          className="inline-flex items-center gap-1.5 rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover disabled:cursor-not-allowed disabled:opacity-40"
        >
          <PlusCircle className="h-4 w-4" aria-hidden />
          {t('safe.deposit')}
        </button>
        <button
          type="button"
          onClick={() => setRecording('OWNER_WITHDRAWAL')}
          disabled={!canManage || !balance}
          title={canManage ? undefined : t('safe.noManagePermission')}
          className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-40"
        >
          <MinusCircle className="h-4 w-4" aria-hidden />
          {t('safe.withdraw')}
        </button>
      </header>

      <div className="min-h-0 flex-1 space-y-6 px-4 pb-6">
        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : loading && !balance ? (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        ) : balance ? (
          <SafeBalanceCard balance={balance} currencySymbol={currencySymbol} />
        ) : (
          <p role="alert" className="text-sm text-danger">
            {t('safe.balanceCard.loadFailed')}
          </p>
        )}

        <SafeTransactionsSection
          transactions={transactions}
          error={transactionsError}
          currencySymbol={currencySymbol}
        />
      </div>

      {recording && balance ? (
        <RecordSafeTransactionDialog
          kind={recording}
          balanceMd={balance.balanceMd}
          currencySymbol={currencySymbol}
          onClose={() => setRecording(null)}
          onRecorded={handleRecorded}
        />
      ) : null}
    </div>
  );
}

/**
 * Balance + reconciliation. Exported so the drift rules — the warning appears
 * exactly when driftMd is non-zero, and the three figures are all shown — can
 * be pinned by tests without fetching.
 */
export function SafeBalanceCard({
  balance,
  currencySymbol,
}: {
  balance: SafeBalanceDto;
  currencySymbol: string;
}) {
  const { t } = useTranslation();
  const money = (value: string) => formatIqd(fromMillidinars(value), { currencySymbol });
  const hasDrift = BigInt(balance.driftMd) !== 0n;

  return (
    <section className="rounded-card border border-subtle bg-raised p-4">
      <div className="flex flex-wrap items-baseline gap-x-8 gap-y-2">
        <div>
          <div className="text-sm text-content-secondary">{t('safe.balanceCard.balance')}</div>
          {/* The safe's balance is THE figure of this screen — it glows. */}
          <div className="tabular text-2xl font-bold text-primary [text-shadow:var(--glow-primary)]">
            {money(balance.balanceMd)}
          </div>
        </div>
        <div>
          <div className="text-sm text-content-secondary">{t('safe.balanceCard.ledgerSum')}</div>
          <div className="tabular text-lg font-medium text-content">
            {money(balance.ledgerSumMd)}
          </div>
        </div>
        <div>
          <div className="text-sm text-content-secondary">{t('safe.balanceCard.voucherCount')}</div>
          <div className="tabular text-lg font-medium text-content">{balance.transactionCount}</div>
        </div>
      </div>

      {hasDrift ? (
        <p
          role="alert"
          className="mt-3 flex items-start gap-2 rounded-card border border-danger/40 p-3 text-sm text-danger"
        >
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            {t('safe.balanceCard.drift', {
              balance: money(balance.balanceMd),
              ledger: money(balance.ledgerSumMd),
              drift: money(balance.driftMd),
            })}
          </span>
        </p>
      ) : (
        <p className="mt-3 text-xs text-content-muted">{t('safe.balanceCard.reconciled')}</p>
      )}
    </section>
  );
}

/**
 * The voucher history. Exported for tests: withdrawals render signed negative,
 * a positive movement carries an explicit "+", and a failed load renders its
 * error IN the section (error-before-empty) rather than vanishing.
 */
export function SafeTransactionsSection({
  transactions,
  error,
  currencySymbol,
}: {
  transactions: SafeTransactionDto[] | null;
  error: string | null;
  currencySymbol: string;
}) {
  const { t } = useTranslation();
  const money = (value: string) => formatIqd(fromMillidinars(value), { currencySymbol });

  if (error) {
    return (
      <section>
        <h2 className="mb-2 text-base font-semibold text-content">
          {t('safe.transactions.title')}
        </h2>
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      </section>
    );
  }
  if (!transactions) return null;

  return (
    <section>
      <h2 className="mb-2 text-base font-semibold text-content">{t('safe.transactions.title')}</h2>

      {transactions.length === 0 ? (
        <p className="text-sm text-content-muted">{t('safe.transactions.empty')}</p>
      ) : (
        <div className="overflow-x-auto rounded-card border border-subtle">
          <table className="w-full text-sm">
            <thead className="bg-overlay text-[10px] uppercase tracking-[0.14em] text-content-muted">
              <tr>
                <Th align="end">{t('safe.transactions.voucher')}</Th>
                <Th>{t('safe.transactions.when')}</Th>
                <Th>{t('safe.transactions.kind')}</Th>
                <Th>{t('safe.transactions.employee')}</Th>
                <Th>{t('safe.transactions.note')}</Th>
                <Th>{t('safe.transactions.businessDate')}</Th>
                <Th align="end">{t('safe.transactions.amount')}</Th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((row) => (
                <tr key={row.id} className="border-t border-subtle">
                  <Td align="end" strong>
                    {row.voucherNo}
                  </Td>
                  <Td>{new Date(row.createdAt).toLocaleString()}</Td>
                  <Td>{t(`safe.kinds.${row.kind}`)}</Td>
                  <Td>{row.employeeName}</Td>
                  <Td>{row.note ?? '—'}</Td>
                  <Td>{row.businessDate}</Td>
                  <Td align="end" strong>
                    <span className={BigInt(row.amountMd) < 0n ? 'text-danger' : undefined}>
                      {signedMoney(row.amountMd, money)}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * Ledger amounts are signed judgements: "+" is written out for money in, so
 * a column of movements can never be misread as unsigned magnitudes.
 */
export function signedMoney(md: string, money: (value: string) => string): string {
  return BigInt(md) > 0n ? `+${money(md)}` : money(md);
}

function Th({ children, align }: { children?: React.ReactNode; align?: 'end' }) {
  return (
    <th className={`px-3 py-2 font-medium ${align === 'end' ? 'text-end' : 'text-start'}`}>
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  strong,
}: {
  children?: React.ReactNode;
  align?: 'end';
  strong?: boolean;
}) {
  return (
    <td
      className={[
        'px-3 py-2',
        align === 'end' ? 'tabular text-end' : 'text-start',
        strong ? 'font-medium text-content' : 'text-content-secondary',
      ].join(' ')}
    >
      {children}
    </td>
  );
}
